// @owners { team: patients }
import { useEffect, useRef } from 'react';
import { type TrackProps, useAnalytics } from './useAnalytics';

/**
 * Fires a "page viewed" analytics event once — on mount, or the first time `enabled`
 * becomes true.
 *
 * Encapsulates the mount-once + latest-args pattern so call sites don't need a hand-rolled
 * `useEffect(() => { trackPageView(...) }, [])` with a `react-hooks/exhaustive-deps` disable
 * (which React Compiler rejects). Behaviorally identical to the old pattern: `trackPageView`
 * keeps its own once-per-instance guard, and `enabled` gates the fire until ready.
 *
 * @example
 * usePageViewedEvent({ event: EVENTS.MED_DETAILS__VIEWED, params: { medicationName } });
 *
 * @example // gate until data is loaded
 * usePageViewedEvent({ event: EVENTS.X_VIEWED, params: { id }, enabled: !!data });
 */
export const usePageViewedEvent = ({ event, params, additionalFields, enabled = true }: TrackProps) => {
  const { trackPageView } = useAnalytics();

  // Keep the latest args in a ref so the fire-once effect below doesn't have to list them as
  // dependencies (params is usually a fresh object each render). Updated in an effect — never
  // mutated during render — so it stays within the Rules of React.
  const latestArgs = useRef({ event, params, additionalFields });
  useEffect(() => {
    latestArgs.current = { event, params, additionalFields };
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const { event: latestEvent, params: latestParams, additionalFields: latestAdditionalFields } = latestArgs.current;
    trackPageView({ event: latestEvent, params: latestParams, additionalFields: latestAdditionalFields });
  }, [enabled, trackPageView]);
};
